// React
import { useEffect, useMemo, useState } from "react";

// Third Party
import { SnackbarProvider } from "notistack";

// Hooks
import { gql, useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useAccessSettingsUsersPaginatedQuery } from "@Common/hooks/Users/useAccessSettingsUsersPaginatedQuery";
import { useLocation } from "@Common/hooks/Location/useLocation";

// Components
import { AUTO_HIDE_DURATION, MAX_SNACK, SnackbarsCustomStyle } from "@Layouts/components/Snackbars";
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import { LoadingSpinner } from "@Layouts/components/LoadingSpinner";
import UserAccessSettingsModel from "./UserAccessSettingsModel";

// Types
import { IUser } from "@Common/interfaces/user";

// Assets
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

function getUsersPositions(users: IUser[] = []): string[] {
	const existingPositions = users.map((element) => element.position ?? "").filter(Boolean);
	if (!existingPositions.length) {
		return [];
	}

	return [...new Set(existingPositions)].sort();
}

function getPositionTree(users: IUser[] = [], positions: string[] = []) {
	return positions?.map((position) => {
		const list = users.filter((element) => element.position === position);
		return {
			_id: position,
			fullName: position,
			bold: true,
			disabled: !list?.length,
			list,
		};
	});
}

const QUERY_IDENTIFIERS = ["usersAccess", "userAccessSettings"];

export default function UserAccessSettingsLayer() {
	const { clearCache } = useCacheCleaner();
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	const location = useLocation();

	// States
	const [user, setUser] = useState<IUser | null>(null);
	const [open, setOpen] = useState(true);

	const { data: users, isLoading, isFetching } = useAccessSettingsUsersPaginatedQuery({ location });

	const userPositionsTree = useMemo(() => {
		const positions = getUsersPositions(users ?? []);
		if (!positions?.length) {
			return [];
		}
		const positionTree = getPositionTree(users ?? [], positions);
		if (isFetching) {
			return [
				{
					_id: "",
					fullName: <LoadingSpinner label={`Users are still loading...`} className="items-center" />,
					bold: false,
					disabled: true,
					list: [],
				},
				...positionTree,
			];
		}
		return positionTree;
	}, [users, isFetching]);

	const handleSelection = (selected: IUser) => {
		if (selected?._id !== user?._id) {
			setOpen(!open);
			setUser(selected);

			addSubscription(
				["userAccessSettings", selected._id],
				[
					{
						context: "onAddUserAuthorization",
						query: gql`
							subscription onAddUserAuthorization($_id: String) {
								onAddUserAuthorization(_id: $_id) {
									_id
									auth {
										_id
										locationId
										actions {
											_id
											actionDescription
											actionKey
											actionName
											category
										}
										groups {
											_id
											groupActions
											groupDescription
											groupName
											locationId
										}
									}
								}
							}
						`,
						variables: {
							_id: selected._id,
						},
					},
					{
						context: "onDeleteUserAuthorization",
						query: gql`
							subscription onDeleteUserAuthorization($_id: String) {
								onDeleteUserAuthorization(_id: $_id) {
									_id
									auth {
										_id
										locationId
										actions {
											_id
											actionDescription
											actionKey
											actionName
											category
										}
										groups {
											_id
											groupActions
											groupDescription
											groupName
											locationId
										}
									}
								}
							}
						`,
						variables: {
							_id: selected._id,
						},
					},
					{
						context: "onAddUserAuthorizationLocation",
						query: gql`
							subscription onAddUserAuthorizationLocation($_id: String) {
								onAddUserAuthorizationLocation(_id: $_id) {
									_id
									auth {
										_id
										locationId
										actions {
											_id
											actionDescription
											actionKey
											actionName
											category
										}
										groups {
											_id
											groupActions
											groupDescription
											groupName
											locationId
										}
									}
								}
							}
						`,
						variables: {
							_id: selected._id,
						},
					},
					{
						context: "onDeleteUserAuthorizationLocation",
						query: gql`
							subscription onDeleteUserAuthorizationLocation($_id: String) {
								onDeleteUserAuthorizationLocation(_id: $_id) {
									_id
									auth {
										_id
										locationId
									}
								}
							}
						`,
						variables: {
							_id: selected._id,
						},
					},
				]
			);
		}
	};

	useEffect(() => {
		if (!location) {
			return;
		}

		return () => {
			setOpen(true);
			setUser(null);
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [location, clearSubscriptionContext]);

	return (
		<SnackbarProvider
			maxSnack={MAX_SNACK}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			autoHideDuration={AUTO_HIDE_DURATION}
			iconVariant={{
				error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
				success: <DoneIcon fontSize="small" className="mr-2" />,
			}}
			Components={{
				error: SnackbarsCustomStyle,
				success: SnackbarsCustomStyle,
			}}
		>
			<BodyLayer
				floatingContent={
					<CollapsibleLeftPanel
						permanentTrigger={open}
						open={true}
						control={
							<AppSideBar
								searchProps={{
									searchBy: "fullName",
									loading: isLoading,
								}}
								sideBarProps={{
									tree: userPositionsTree || [],
									showBy: "fullName",
									loading: isLoading,
									selected: user?._id ?? "",
									onSelectAction: handleSelection,
								}}
							/>
						}
					/>
				}
			>
				<RoundedBox sx={{ minHeight: "630px" }}>
					<UserAccessSettingsModel selectedUser={user} isLoadingSideBar={isLoading} location={location} />
				</RoundedBox>
			</BodyLayer>
		</SnackbarProvider>
	);
}
